import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, 
  Alert, Container, Paper, CircularProgress,
  InputAdornment, IconButton
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import api from '../api';
import { TENANT_PASSWORD_HINT, tenantPasswordError } from '../utils/tenantPassword';
import { saveTenantSession } from '../utils/tenantSession';

const TenantActivation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [tenantInfo, setTenantInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const verifyToken = async () => {
    if (!token) {
      setError('Invalid invitation link. Please check your email and try again.');
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Verifying invitation token...');
      const response = await api.get(`/api/tenant-invitations/verify-token/${token}`);
      console.log('✅ Token verified:', response.data);
      setTenantInfo(response.data);
      setLoading(false);
    } catch (err) {
      console.error('❌ Token verification failed:', err);
      setError(err.response?.data?.error || 'Invalid or expired invitation link');
      setLoading(false);
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordError = tenantPasswordError(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);

    try {
      console.log('🔐 Activating account...');
      
      const response = await api.post('/api/tenant-invitations/activate', {
        token,
        password
      });
      saveTenantSession(response.data);

      console.log('✅ Account activated successfully');

      setSuccess(true);

      // Redirect to tenant portal after 2 seconds (already signed in via the saved session)
      setTimeout(() => {
        window.location.href = '/tenant-portal';
      }, 2000);
      
    } catch (err) {
      console.error('❌ Activation failed:', err);
      setError(err.response?.data?.error || 'Failed to activate account. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Verifying invitation...</Typography>
        </Box>
      </Container>
    );
  }

  if (error && !tenantInfo) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Alert severity="error">
              <Typography variant="h6" gutterBottom>
                Invalid Invitation
              </Typography>
              <Typography variant="body2">
                {error}
              </Typography>
              <Button 
                variant="outlined" 
                sx={{ mt: 2 }}
                onClick={() => window.location.href = '/tenant-portal'}
              >
                Go to Login
              </Button>
            </Alert>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom color="success.main">
              Account Activated!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your tenant portal account has been successfully activated.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Redirecting to login page...
            </Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" gutterBottom>
              Welcome to Your Tenant Portal
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Hi {tenantInfo?.firstName} {tenantInfo?.lastName},
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please create a password to activate your account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleActivate}>
            <TextField
              fullWidth
              label="Email (Your Username)"
              value={tenantInfo?.email || ''}
              margin="normal"
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Create Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText={TENANT_PASSWORD_HINT}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ mt: 3 }}
            >
              {submitting ? 'Activating Account...' : 'Activate Account'}
            </Button>
          </form>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              <strong>What you'll get access to:</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              • Submit maintenance requests with photo uploads
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              • View your lease information and payment history
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              • Receive notifications and messages
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              • Track your rental account 24/7
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TenantActivation;

